import { useMemo } from 'react';
import { Banner, Box, Checkbox, Flex, Heading, Panel, PanelSection } from '@customerio/pluma-components/react';
import { Controller, useFormContext } from '@/components/form';
import { GroupedChecklist } from '@/components/grouped-checklist';
import { getDataOutEventGroups } from '@/utils/data-out-events';
import * as styles from './events-section.css';

type EventsSectionProps = {
  isDisabled: boolean;
};

/** Which events the connector forwards, and the body-content option that travels with them. */
export function EventsSection({ isDisabled }: EventsSectionProps) {
  const { control } = useFormContext();
  const groups = useMemo(() => [...getDataOutEventGroups()], []);

  return (
    <PanelSection>
      <Flex gap="300" alignItems="flex-start">
        <Box className={styles.eventsColumn}>
          <Heading level="4" mb="150">
            Select events
          </Heading>
          <Controller
            name="events"
            control={control}
            render={({ field }) => {
              const selected: string[] = Array.isArray(field.value) ? field.value : [];
              return (
                <>
                  {selected.length === 0 ? (
                    <Banner
                      variant="caution"
                      icon={null}
                      my="150"
                      title="No events selected"
                      description="If no events are selected, this integration will be disabled."
                    />
                  ) : null}
                  <GroupedChecklist
                    groups={groups}
                    selectedItems={selected}
                    isDisabled={isDisabled}
                    onChange={(delta) => {
                      field.onChange(delta.selectedItems);
                    }}
                  />
                </>
              );
            }}
          />
        </Box>

        <Box>
          <Heading level="4" mb="150">
            Options
          </Heading>
          <Panel backgroundColor="subtle">
            <PanelSection>
              <Heading level="5" mb="150">
                Body content
              </Heading>
              <Controller
                name="with_content"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    isChecked={field.value === true}
                    isDisabled={isDisabled}
                    label="Include body content and headers in all Sent events"
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                    }}
                  />
                )}
              />
            </PanelSection>
          </Panel>
        </Box>
      </Flex>
    </PanelSection>
  );
}
